import React from "react";
import { Box, Chip, Divider, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Send } from "lucide-react";
import UserAvatar from "./UserAvatar";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import ActivityAlertStrip from "./ActivityAlertStrip";
import DateRangeSelect from "./DateRangeSelect";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "custom", label: "Custom" },
  { key: "total", label: "Total" },
];

// Small badge that tells the user which time window a card's number covers.
function ScopeChip({ scope }) {
  if (!scope) return null;
  return (
    <Box
      sx={{
        px: 0.75,
        py: "1px",
        borderRadius: "5px",
        backgroundColor: "hsl(140 6% 93%)",
        border: "1px solid hsl(140 8% 86%)",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "hsl(150 10% 46%)",
          lineHeight: 1.6,
          whiteSpace: "nowrap",
        }}
      >
        {scope}
      </Typography>
    </Box>
  );
}

const labelSx = {
  color: "hsl(150 10% 46%)",
  fontSize: 12,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: 0.5,
};

const cardSx = {
  border: "1px solid hsl(140 8% 88%)",
  borderRadius: "0.75rem",
  backgroundColor: "#fff",
  p: 2.5,
};

const sectionLabelSx = {
  ...labelSx,
  fontSize: 13,
  mb: 0,
};

const tableHeadCellSx = {
  backgroundColor: "transparent",
  borderBottom: "1px solid hsl(140 8% 88%)",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "hsl(150 10% 46%)",
  py: "10px",
  px: "16px",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  whiteSpace: "nowrap",
  py: "12px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  borderBottom: "1px solid hsl(140 8% 88%)",
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function DeltaBadge({ current, previous, label, comparisonLabel, comparable = true, scope }) {
  const diff = (current || 0) - (previous || 0);
  const up = diff >= 0;
  const hasBaseline = previous > 0;
  const pct = hasBaseline ? Math.round(Math.abs(diff / previous) * 100) : 0;
  // No comparison when the period is "total" (nothing to compare against).
  const isNew = comparable && !hasBaseline && diff > 0;
  const showDelta = comparable && !isNew && pct > 0;

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      sx={{
        border: "1px solid hsl(140 8% 88%)",
        borderRadius: "0.75rem",
        overflow: "hidden",
        backgroundColor: "#fff",
        "&:hover": { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
      }}
    >
      <Box sx={{ height: 4, width: "100%", backgroundColor: up ? "hsl(142 71% 45%)" : "hsl(0 72% 51%)" }} />
      <Box sx={{ p: "1.25rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, flexWrap: "wrap" }}>
          <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {label}
          </Typography>
          <ScopeChip scope={scope} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ color: "hsl(150 30% 12%)", fontSize: 22, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", lineHeight: 1.2 }}>
            {current ?? 0}
          </Typography>
          {isNew && (
            <Box sx={{ px: 1, py: 0.25, borderRadius: "6px", backgroundColor: "hsl(142 60% 92%)", border: "1px solid hsl(142 60% 80%)" }}>
              <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontWeight: 600, color: "#079344" }}>
                New
              </Typography>
            </Box>
          )}
          {showDelta && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                backgroundColor: up ? "hsl(142 60% 92%)" : "hsl(0 70% 94%)",
                border: "1px solid",
                borderColor: up ? "hsl(142 60% 80%)" : "hsl(0 70% 80%)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  fontWeight: 600,
                  color: up ? "#079344" : "#D32F2F",
                  whiteSpace: "nowrap",
                }}
              >
                {up ? "↑" : "↓"}{pct}% {comparisonLabel || ""}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function KpiCard({ label, value, highlighted, warning, barColor, scope }) {
  return (
    <Box
      component={motion.div}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      sx={{
        border: "1px solid hsl(140 8% 88%)",
        borderRadius: "0.75rem",
        overflow: "hidden",
        backgroundColor: "#fff",
        ...(warning && { borderColor: "hsl(36 90% 70%)", backgroundColor: "hsl(36 90% 97%)" }),
        "&:hover": { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
      }}
    >
      <Box sx={{ height: 4, width: "100%", backgroundColor: barColor || (warning ? "hsl(38 92% 50%)" : highlighted ? "hsl(150 38% 17%)" : "#fff") }} />
      <Box sx={{ p: "1.25rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
          <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {label}
          </Typography>
          <ScopeChip scope={scope} />
        </Box>
        <Typography sx={{ color: warning ? "#F57C00" : "hsl(150 30% 12%)", fontSize: 26, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", lineHeight: 1.2 }}>
          {value ?? "None"}
        </Typography>
      </Box>
    </Box>
  );
}

// Distinct section: how many applications the talent team sent on this company's
// behalf (count + trend), broken down by the internal user who sent them.
function TalentTeamSendsSection({ data, scope, comparisonLabel, comparable, onOpen }) {
  const total = data?.total || 0;
  const previous = data?.previous || 0;
  const byUser = data?.by_user || [];
  const attributed = byUser.reduce((sum, u) => sum + (u.count || 0), 0);
  const unattributed = Math.max(0, total - attributed);
  const maxCount = Math.max(1, ...byUser.map((u) => u.count || 0), unattributed);

  const diff = total - previous;
  const up = diff >= 0;
  const hasBaseline = previous > 0;
  const pct = hasBaseline ? Math.round(Math.abs(diff / previous) * 100) : 0;
  const isNew = comparable && !hasBaseline && diff > 0;
  const showDelta = comparable && !isNew && pct > 0;

  const barRow = (key, label, count, user) => (
    <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75 }}>
      {user ? (
        <UserAvatar user={user} size={28} />
      ) : (
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "hsl(140 6% 90%)", flexShrink: 0 }} />
      )}
      <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", minWidth: 0, flexShrink: 0, width: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: "hsl(140 6% 93%)", overflow: "hidden" }}>
        <Box sx={{ width: `${Math.round((count / maxCount) * 100)}%`, height: "100%", borderRadius: 4, backgroundColor: user ? "hsl(150 38% 17%)" : "hsl(140 8% 78%)" }} />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", minWidth: 24, textAlign: "right" }}>
        {count}
      </Typography>
    </Box>
  );

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut", delay: 0.04 }}
    >
      <Typography sx={{ ...labelSx, mb: 1.5, fontSize: 13 }}>Talent Team</Typography>

      <Box
        onClick={onOpen}
        sx={{
          border: "1px solid hsl(140 8% 88%)",
          borderRadius: "0.75rem",
          overflow: "hidden",
          backgroundColor: "#fff",
          cursor: onOpen ? "pointer" : "default",
          transition: "box-shadow 150ms ease",
          "&:hover": onOpen ? { boxShadow: "0 1px 3px rgba(0,0,0,0.08)" } : {},
        }}
      >
        <Box sx={{ height: 4, width: "100%", backgroundColor: "hsl(150 38% 17%)" }} />
        <Box sx={{ p: "1.25rem" }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Send size={15} color="hsl(150, 38%, 17%)" />
            <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Talent-team Sends
            </Typography>
            <ScopeChip scope={scope} />
            <Box sx={{ flex: 1 }} />
            {onOpen && (
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "hsl(150 38% 17%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                View applications →
              </Typography>
            )}
          </Box>

          {/* Count + trend */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: total > 0 ? 1.5 : 0 }}>
            <Typography sx={{ color: "hsl(150 30% 12%)", fontSize: 26, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", lineHeight: 1.2 }}>
              {total}
            </Typography>
            {isNew && (
              <Box sx={{ px: 1, py: 0.25, borderRadius: "6px", backgroundColor: "hsl(142 60% 92%)", border: "1px solid hsl(142 60% 80%)" }}>
                <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontWeight: 600, color: "#079344" }}>New</Typography>
              </Box>
            )}
            {showDelta && (
              <Box sx={{ px: 1, py: 0.25, borderRadius: "6px", backgroundColor: up ? "hsl(142 60% 92%)" : "hsl(0 70% 94%)", border: "1px solid", borderColor: up ? "hsl(142 60% 80%)" : "hsl(0 70% 80%)" }}>
                <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontWeight: 600, color: up ? "#079344" : "#D32F2F", whiteSpace: "nowrap" }}>
                  {up ? "↑" : "↓"}{pct}% {comparisonLabel || ""}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Per-internal-user breakdown */}
          {total > 0 ? (
            <>
              <Divider sx={{ borderColor: "hsl(140 8% 90%)", mb: 0.5 }} />
              {byUser.map((u) => barRow(u.id, u.full_name, u.count, u))}
              {unattributed > 0 && barRow("unattributed", "Unattributed", unattributed, null)}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", py: 1 }}>
              No talent-team sends in this period
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function SkeletonPanel() {
  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={cardSx}>
            <Skeleton width="60%" height={16} />
            <Skeleton width="40%" height={32} sx={{ mt: 0.5 }} />
          </Box>
        ))}
      </Box>
      <Box sx={cardSx}>
        <Skeleton width="30%" height={16} />
        <Skeleton width="100%" height={140} sx={{ mt: 1 }} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "16px" }}>
        {[0, 1].map((i) => (
          <Box key={i} sx={cardSx}>
            <Skeleton width="50%" height={16} />
            <Skeleton width="30%" height={28} sx={{ mt: 0.5 }} />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

const CompanyInsightsPanel = ({
  companyMetrics,
  loading,
  recruiterInsights,
  refreshing,
  period = "month",
  customRange,
  onPeriodChange,
  onCustomRangeChange,
  onOpenJobApplications,
  featureAdoption,
  activityLog,
  timeOnPlatform,
}) => {
  const [section, setSection] = React.useState("activity");

  if (loading || !companyMetrics) return <SkeletonPanel />;

  const {
    kpis,
    weekly_visits: weeklyVisits,
    activity_breakdown: breakdown,
    credit_health: creditHealth,
    kanban_usage: kanban,
    kanban_stage_durations: stageDurations,
    candidate_interests: interests,
    cv_downloads: downloads,
    talent_team_sends: talentTeamSends,
    suggestions,
    internal_activities: activities,
    alerts,
    recruiter_activity_table: recruiterActivityTable,
    licence_utilisation_pct: licenceUtilisationPct,
    last_login_days_ago: lastLoginDaysAgo,
    range,
  } = companyMetrics;

  const rangeLabel = range?.label || "This month";
  const comparisonLabel = range?.comparison_label;
  const comparable = range?.comparable !== false;
  const ALL_TIME = "All-time";
  const CURRENT = "Current";

  const sectionTabs = [
    { key: "activity", label: "Activity" },
    ...(talentTeamSends ? [{ key: "talent_team", label: "Talent Team" }] : []),
    ...(interests || downloads ? [{ key: "contact_interests", label: "Contact Interests" }] : []),
    ...(creditHealth ? [{ key: "credits", label: "Credits" }] : []),
    ...(featureAdoption ? [{ key: "feature_adoption", label: "Feature adoption" }] : []),
    ...(activityLog ? [{ key: "activity_log", label: "Activity log" }] : []),
    ...(timeOnPlatform ? [{ key: "time_on_platform", label: "Time on platform" }] : []),
  ];
  const activeSection = sectionTabs.some((t) => t.key === section) ? section : "activity";

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      {/* Header + time-frame selector */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2, color: "hsl(150 30% 12%)", lineHeight: 1.2, fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
            Activity
          </Typography>
          <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", mt: 0.5, fontFamily: "Segoe UI regular, sans-serif, system-ui" }}>
            Showing {rangeLabel.toLowerCase()}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {period === "custom" && (
            <DateRangeSelect
              startDate={customRange?.startDate}
              endDate={customRange?.endDate}
              onChange={(r) => onCustomRangeChange?.(r)}
            />
          )}
          <Box
            sx={{
              display: "flex",
              backgroundColor: "hsl(140 6% 90%)",
              borderRadius: "8px",
              p: "2px",
              border: "1px solid hsl(140 8% 88%)",
            }}
          >
            {PERIODS.map((p) => (
              <Box
                key={p.key}
                component="button"
                onClick={() => onPeriodChange?.(p.key)}
                sx={{
                  all: "unset",
                  px: "16px",
                  py: "6px",
                  fontSize: 12,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 150ms ease",
                  color: period === p.key ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                  backgroundColor: period === p.key ? "#ffffff" : "transparent",
                  boxShadow: period === p.key ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  "&:hover": { color: "hsl(150 30% 12%)" },
                }}
              >
                {p.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {sectionTabs.length > 1 && (
        <Box sx={{ display: "flex", gap: "4px", borderBottom: "1px solid hsl(140 8% 88%)" }}>
          {sectionTabs.map((t) => (
            <Box
              key={t.key}
              component="button"
              onClick={() => setSection(t.key)}
              sx={{
                all: "unset",
                px: "14px",
                py: "8px",
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                cursor: "pointer",
                color: activeSection === t.key ? "hsl(150 38% 17%)" : "hsl(150 10% 46%)",
                borderBottom: activeSection === t.key ? "2px solid hsl(150 38% 17%)" : "2px solid transparent",
                mb: "-1px",
                transition: "all 150ms ease",
                "&:hover": { color: "hsl(150 30% 12%)" },
              }}
            >
              {t.label}
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          opacity: refreshing ? 0.5 : 1,
          transition: "opacity 150ms ease",
          pointerEvents: refreshing ? "none" : "auto",
        }}
      >
        <Stack spacing={3}>
      {activeSection === "activity" && (
        <>
      {/* Activity Alerts */}
      <ActivityAlertStrip alerts={alerts} />

      {/* Account Health */}
      {/* Licence Utilisation = used (activated) credits / total credits * 100, how much of what they paid for is used */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "16px" }}>
        <KpiCard
          label="Licence Utilisation"
          value={licenceUtilisationPct != null ? `${licenceUtilisationPct}%` : "None"}
          warning={licenceUtilisationPct != null && licenceUtilisationPct < 50}
          scope={ALL_TIME}
        />
        <KpiCard
          label="Days Since Last Login"
          value={lastLoginDaysAgo != null ? lastLoginDaysAgo : "Never"}
          warning={lastLoginDaysAgo == null || lastLoginDaysAgo > 14}
          scope={CURRENT}
        />
      </Box>

      {/* KPIs */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0 }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
          <DeltaBadge label="Recruiter Visits" current={breakdown?.visits_this_month} previous={breakdown?.visits_last_month} comparisonLabel={comparisonLabel} comparable={comparable} scope={rangeLabel} />
          <KpiCard label="Job Offers" value={kpis?.job_offers ?? 0} barColor="hsl(38 92% 50%)" scope={rangeLabel} />
          <DeltaBadge label="Applications Received" current={kpis?.applications_this_month} previous={kpis?.applications_last_month} comparisonLabel={comparisonLabel} comparable={comparable} scope={rangeLabel} />
          <KpiCard label="Candidates Added" value={kpis?.candidates_added ?? 0} barColor="hsl(0 72% 51%)" scope={rangeLabel} />
        </Box>
      </Box>

        </>
      )}

      {/* Talent-team Sends (distinct section) */}
      {activeSection === "talent_team" && talentTeamSends && (
        <TalentTeamSendsSection
          data={talentTeamSends}
          scope={rangeLabel}
          comparisonLabel={comparisonLabel}
          comparable={comparable}
          onOpen={onOpenJobApplications}
        />
      )}

      {activeSection === "activity" && (
        <>
      {/* Usage Trend + Pipeline Funnel */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.08 }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: "16px" }}>
          <Box sx={cardSx}>
            <Typography sx={{ ...labelSx, mb: 1.5 }}>Usage Trend (Last 6 Months)</Typography>
            {weeklyVisits && weeklyVisits.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={weeklyVisits} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif", fill: "hsl(150 10% 46%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif", fill: "hsl(150 10% 46%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid hsl(140 8% 88%)",
                      fontFamily: "Segoe UI Regular, sans-serif",
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="visits" fill="hsl(150 38% 17%)" radius={[4, 4, 0, 0]} maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", py: 4, textAlign: "center" }}>
                No visit data available
              </Typography>
            )}
          </Box>
          <Box sx={cardSx}>
            <Typography sx={{ ...labelSx, mb: 1.5 }}>Pipeline Funnel (Last 30 days)</Typography>
            {stageDurations && stageDurations.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={stageDurations} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif", fill: "hsl(150 10% 46%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={90}
                    tick={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif", fill: "hsl(150 10% 46%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid hsl(140 8% 88%)",
                      fontFamily: "Segoe UI Regular, sans-serif",
                      fontSize: 12,
                    }}
                    formatter={(value) => [`${value} candidates`, "Entered"]}
                  />
                  <Bar dataKey="entered_count" fill="hsl(220 70% 55%)" radius={[0, 4, 4, 0]} maxBarSize={24} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", py: 4, textAlign: "center" }}>
                No pipeline data available
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

        </>
      )}

      {/* Credit Health */}
      {activeSection === "credits" && creditHealth && (
        <>
          <Typography sx={sectionLabelSx}>Credit Health (Licence)</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(5, 1fr)" }, gap: "16px" }}>
            <KpiCard label="Total" value={creditHealth.total} highlighted scope={ALL_TIME} />
            <KpiCard label="Available" value={creditHealth.available} scope={CURRENT} />
            <KpiCard label="Activated" value={creditHealth.activated} scope={CURRENT} />
            <KpiCard label="Expired" value={creditHealth.expired} scope={CURRENT} />
            <KpiCard label="Expiring Soon" value={creditHealth.expiring_soon} warning={creditHealth.expiring_soon > 0} scope={CURRENT} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "16px", mt: 2 }}>
            <DeltaBadge label="Credits Used" current={creditHealth.burn_rate_this_month} previous={creditHealth.burn_rate_last_month} comparisonLabel={comparisonLabel} comparable={comparable} scope={rangeLabel} />
            <KpiCard
              label="CV Access"
              value={creditHealth.cv_access ? `${creditHealth.cv_access.status}, expires ${creditHealth.cv_access.expire_at || "N/A"}` : "None"}
              highlighted={!!creditHealth.cv_access}
              scope={CURRENT}
            />
          </Box>
        </>
      )}

      {/* Pipeline Activity */}
      {activeSection === "activity" && kanban && (
        <>
          <Typography sx={sectionLabelSx}>Pipeline Activity</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
            <KpiCard label="Total Cards" value={kanban.total_cards} highlighted scope={ALL_TIME} />
            <KpiCard label="Total Moves" value={kanban.total_moves} scope={ALL_TIME} />
            <DeltaBadge label="Cards Moved" current={kanban.moves_this_month} previous={kanban.moves_last_month} comparisonLabel={comparisonLabel} comparable={comparable} scope={rangeLabel} />
            <KpiCard label="Avg. Time in Pending" value={kanban.avg_pending_days != null ? `${kanban.avg_pending_days}d` : "None"} scope={ALL_TIME} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px", mt: 2 }}>
            <KpiCard label="Job Applications Total" value={kanban.total_applications} highlighted scope={ALL_TIME} />
            <KpiCard label="Closed Total" value={kanban.apps_closed_total} scope={ALL_TIME} />
            <DeltaBadge label="Closed" current={kanban.apps_closed_this_month} previous={kanban.apps_closed_last_month} comparisonLabel={comparisonLabel} comparable={comparable} scope={rangeLabel} />
            <KpiCard label="Marked as Hired" value={kanban.cards_in_hired} scope={ALL_TIME} />
          </Box>
        </>
      )}

      {/* Candidate Interests (CV Reveals) */}
      {activeSection === "contact_interests" && interests && (
        <>
          <Typography sx={sectionLabelSx}>CV Library Usage (Reveals)</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "16px" }}>
            <KpiCard label="Total Reveals" value={interests.total} highlighted scope={ALL_TIME} />
            <DeltaBadge label="Reveals" current={interests.this_month} previous={interests.last_month} comparisonLabel={comparisonLabel} comparable={comparable} scope={rangeLabel} />
          </Box>
        </>
      )}

      {activeSection === "contact_interests" && downloads && (
        <>
          <Typography sx={sectionLabelSx}>CV Downloads</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
            <KpiCard label="Unique CV Downloads" value={downloads.cv?.unique_this_month ?? 0} scope={rangeLabel} />
            <KpiCard label="Unique CV Downloads" value={downloads.cv?.unique ?? 0} highlighted scope={ALL_TIME} />
            <KpiCard label="CV Downloads (incl. duplicates)" value={downloads.cv?.this_month ?? 0} scope={rangeLabel} />
            <KpiCard label="CV Downloads (incl. duplicates)" value={downloads.cv?.total ?? 0} scope={ALL_TIME} />
          </Box>

          <Typography sx={sectionLabelSx}>Portfolio Downloads</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
            <KpiCard label="Unique Portfolio Downloads" value={downloads.portfolio?.unique_this_month ?? 0} scope={rangeLabel} />
            <KpiCard label="Unique Portfolio Downloads" value={downloads.portfolio?.unique ?? 0} highlighted scope={ALL_TIME} />
            <KpiCard label="Portfolio Downloads (incl. duplicates)" value={downloads.portfolio?.this_month ?? 0} scope={rangeLabel} />
            <KpiCard label="Portfolio Downloads (incl. duplicates)" value={downloads.portfolio?.total ?? 0} scope={ALL_TIME} />
          </Box>
        </>
      )}

      {/* Suggestions */}
      {activeSection === "activity" && suggestions && (
        <>
          <Typography sx={sectionLabelSx}>Suggestions</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: "16px" }}>
            <KpiCard label="Total Sent" value={suggestions.total} highlighted scope={ALL_TIME} />
            <KpiCard label="Pending" value={suggestions.pending} scope={CURRENT} />
            <KpiCard label="Accepted" value={suggestions.accepted} scope={CURRENT} />
            <KpiCard label="Rejected" value={suggestions.rejected} scope={CURRENT} />
          </Box>
        </>
      )}

      {/* Enhanced Recruiter Activity Table */}
      {/* Inactive = recruiter has not logged in for 30+ days */}
      {activeSection === "activity" && recruiterActivityTable && recruiterActivityTable.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography sx={sectionLabelSx}>Recruiter Activity</Typography>
            <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", fontStyle: "italic" }}>
              Inactive = no login for 30+ days · Logins & reveals reflect {rangeLabel.toLowerCase()}
            </Typography>
          </Box>
          <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Recruiter", "Last Login", "Logins", "CV Reveals", "CV Downloads", "Favorites", "Status"].map((h) => (
                    <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recruiterActivityTable.map((r) => {
                  const inactive = r.status === "inactive";
                  return (
                    <TableRow
                      key={r.id}
                      sx={{
                        backgroundColor: inactive ? "hsl(0 70% 97%)" : "transparent",
                        "&:last-child td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell sx={tableBodyCellSx}>
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                            {r.full_name || `#${r.id}`}
                          </Typography>
                          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                            {r.email || "None"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        {r.last_login ? formatDateTime(r.last_login) : "Never"}
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.logins_this_month}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.cv_reveals_this_month}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.cv_downloads_this_month ?? 0}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.favorites_count ?? 0}</TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Chip
                          label={inactive ? "Inactive" : "Active"}
                          size="small"
                          sx={{
                            backgroundColor: inactive ? "rgba(211,47,47,0.10)" : "rgba(7,147,68,0.10)",
                            color: inactive ? "#D32F2F" : "#079344",
                            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                            fontSize: 10,
                            fontWeight: 600,
                            borderRadius: "6px",
                            height: 20,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </>
      )}


      {/* Recruiter-specific insights (only on recruiter page) */}
      {activeSection === "activity" && recruiterInsights && (
        <>
          <Typography sx={sectionLabelSx}>Recruiter Stats</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }, gap: "16px" }}>
            <KpiCard label="CV Reveals" value={recruiterInsights.candidate_interests_total} highlighted scope={ALL_TIME} />
            <KpiCard label="CV Reveals" value={recruiterInsights.candidate_interests_this_month} scope={rangeLabel} />
            <KpiCard label="CV Downloads" value={recruiterInsights.cv_downloads_total} highlighted scope={ALL_TIME} />
            <KpiCard label="CV Downloads" value={recruiterInsights.cv_downloads_this_month} scope={rangeLabel} />
            <KpiCard label="CV Downloads (unique)" value={recruiterInsights.cv_downloads_unique_total} scope={ALL_TIME} />
            <KpiCard label="Favorite Candidates" value={recruiterInsights.favorite_candidates_count} scope={CURRENT} />
          </Box>
        </>
      )}

      {activeSection === "activity" && (
        <>
      {/* Internal Activity Log */}
      <Typography sx={sectionLabelSx}>Internal Activity Log</Typography>
      {((activities && activities.length > 0) || (recruiterInsights?.internal_activities && recruiterInsights.internal_activities.length > 0)) ? (
        <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Date", "User", "Action", "Target", "Details"].map((h) => (
                    <TableCell key={h} sx={{ ...tableHeadCellSx, position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(recruiterInsights?.internal_activities || activities || []).map((a) => (
                  <TableRow key={a.id} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                    <TableCell sx={tableBodyCellSx}>{formatDateTime(a.created_at)}</TableCell>
                    <TableCell sx={tableBodyCellSx}>{a.internal_user?.full_name || "None"}</TableCell>
                    <TableCell sx={{ ...tableBodyCellSx, fontWeight: 600 }}>{a.action?.replaceAll("_", " ")}</TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      {a.trackable_type ? `${a.trackable_type} #${a.trackable_id}` : "None"}
                    </TableCell>
                    <TableCell sx={{ ...tableBodyCellSx, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.metadata && Object.keys(a.metadata).length > 0
                        ? Object.entries(a.metadata).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(", ")
                        : "None"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : (
        <Box sx={{ ...cardSx, py: 5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
            No internal activity yet.
          </Typography>
        </Box>
      )}
        </>
      )}
        </Stack>
      </Box>
      {activeSection === "feature_adoption" && featureAdoption}
      {activeSection === "activity_log" && activityLog}
      {activeSection === "time_on_platform" && timeOnPlatform}
    </Stack>
  );
};

export default CompanyInsightsPanel;
